export function onContentSwipe(element) {

    let _dragging = false;
    let _didSwipe = false;
    let _intervalId = null;
    let _delta = { x: null, y: null };
    const swipeThreshold = 30;

    const preventIfCancelable = (event) => {
        if (event.cancelable) {
            event.preventDefault();
        }
    };

    const initialize = () => {
        element.addEventListener('mousedown', handleEvent);
        element.addEventListener('touchstart', handleEvent, { passive: true });
    };

    const initializeDragging = () => {
        element.addEventListener('mousemove', handleEvent);
        element.addEventListener('touchmove', handleEvent, { passive: false });
        element.addEventListener('mouseup', handleEvent);
        element.addEventListener('mouseleave', handleEvent);
        element.addEventListener('touchend', handleEvent);
    };

    const cancelDragging = () => {
        if (_intervalId) {
            window.requestAnimationFrame ? 
                window.cancelAnimationFrame(_intervalId) :
                clearInterval(_intervalId);
            _intervalId = null;
        }
        element.removeEventListener('mousemove', handleEvent);
        element.removeEventListener('touchmove', handleEvent);
        element.removeEventListener('mouseup', handleEvent);
        element.removeEventListener('mouseleave', handleEvent);
        element.removeEventListener('touchend', handleEvent);
    };

    const handleEvent = (event) => {
        switch (event.type) {
            case 'mousedown':
            case 'touchstart':
                startDrag(event);
                break;

            case 'mousemove':
            case 'touchmove':
                drag(event);
                break;

            case 'mouseup':
            case 'mouseleave':
            case 'touchend':
                endDrag(event);
                break;
        }
    };


    const getPosition = (event) => {
        return {
            x: parseInt(getEvent(event).clientX),
            y: parseInt(getEvent(event).clientY),
        };
    };

    const getDragDetails = (event) => {
        const currentPosition = getPosition(event);
        return {
            ...currentPosition,
            dx: currentPosition.x - _delta.x,
            dy: currentPosition.y - _delta.y,
        }
    };

    const startDrag = (event) => {
        _dragging = true;
        _didSwipe = false;
        initializeDragging();
        _delta = getPosition(event);
        emitSwipeEvents('dragStart', getDragDetails(event), event.target);
    };


    const drag = (event) => {
        if (!_dragging) {
            return;
        }

        const details = getDragDetails(event);
        if (!_didSwipe && (Math.abs(details.dx) > swipeThreshold || Math.abs(details.dy) > swipeThreshold)) {
            _didSwipe = true;
        }

        if (_didSwipe) {
            preventIfCancelable(event);
        }

        const emitDragEvent = () => {
            emitSwipeEvents('dragging', details);
        }

        // emit dragging event with coordinates
        _intervalId = function () {
            if (window.requestAnimationFrame) {
                return window.requestAnimationFrame(emitDragEvent);
            }
            return setTimeout(emitDragEvent, 250);
        }();
    };

    const endDrag = (event) => {
        cancelDragging();
        // credits: https://css-tricks.com/simple-swipe-with-vanilla-javascript/
        const position = getPosition(event);
  
        // check if there was a left/right swipe
        if (_delta && _delta.x !== null) {
            if (Math.abs(position.x - _delta.x) > swipeThreshold) {
                const xsign = getSign(position.x - _delta.x);
                const eventName = xsign < 0 ? 'swipeLeft' : 'swipeRight';
                emitSwipeEvents(eventName, getDragDetails(event));
                _didSwipe = true;
            }

            _delta.x = null;
        }

        // check if there was a top/bottom swipe
        if (_delta && _delta.y !== null) {
            if (Math.abs(position.y - _delta.y) > swipeThreshold) {
                const ysign = getSign(position.y - _delta.y);
                const eventName = ysign < 0 ? 'swipeUp' : 'swipeDown';
                emitSwipeEvents(eventName, getDragDetails(event));
                _didSwipe = true;
            }

            _delta.y = null;
        }

        if (_didSwipe) {
            preventIfCancelable(event);
        }

        // emit drag end event
        emitSwipeEvents('dragEnd', getDragDetails(event));
        _dragging = false;
        _didSwipe = false;
    };


    // unify mouse and touch events
    const getEvent = (event) => event.changedTouches ? event.changedTouches[0] : event;
    

    function emitSwipeEvents(eventName, detail, el = null) {
        // emit event with coordinates
        const event = new CustomEvent(eventName, { detail: { ...detail, origin: el } });
        element.dispatchEvent(event);
    };

    const getSign = (x) => {
        if (! Math.sign) {
            return ((x > 0) - (x < 0)) || +x;
        }
        return Math.sign(x);
    };

    initialize();


    return () => {
        element.removeEventListener('mousedown', handleEvent);
        element.removeEventListener('touchstart', handleEvent);
        element.removeEventListener('mousemove', handleEvent);
        element.removeEventListener('touchmove', handleEvent);
        element.removeEventListener('mouseup', handleEvent);
        element.removeEventListener('mouseleave', handleEvent);
        element.removeEventListener('touchend', handleEvent);
    };
};


/**
 * onSwipe
 * 
 * @param {HTMLElement} element 
 * @param {Function} callback 
 * @param {Object} options 
 * @returns {Function}
 */

let _isTouchDevice = null;

const getIsTouchDevice = () => {
    if (_isTouchDevice === null) {
        _isTouchDevice = (('ontouchstart' in window) || (navigator.MaxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
    }
    return _isTouchDevice;
};

export function onSwipe(element, callback, options = {}) {
    const {
        threshold = 150,
        restraint = 100,
        allowedTime = 300,
    } = options;

    let startPosition = { x: 0, y: 0 };
    let distance = { x: 0, y: 0 };
    let time = { start: 0, elapsed: 0 };
    let tracking = false;
    const scrollLock = 30;

    const isTouchDevice = getIsTouchDevice();

    const preventIfCancelable = (event) => {
        if (event.cancelable) {
            event.preventDefault();
        }
    };

    const _getDirection = () => {
        if (Math.abs(distance.x) >= threshold && Math.abs(distance.y) <= restraint) {
            return distance.x < 0 ? 'left' : 'right';
        }
        if (Math.abs(distance.y) >= threshold && Math.abs(distance.x) <= restraint) {
            return distance.y < 0 ? 'up' : 'down';
        }
        return null;
    };

    const handleTouchStart = (x, y) => {
        tracking = true;
        startPosition = { x, y };
        time.start = new Date().getTime();
    };

    const handleTouchMove = (event, x, y) => {
        if (!tracking) {
            return;
        }
        const dx = x - startPosition.x;
        const dy = y - startPosition.y;
        if (Math.abs(dx) > scrollLock || Math.abs(dy) > scrollLock) {
            preventIfCancelable(event);
        }
    };

    const handleTouchEnd = (event, x, y) => {
        if (!tracking) {
            return;
        }
        tracking = false;
        distance = {
            x: x - startPosition.x,
            y: y - startPosition.y,
        };
        time.elapsed = new Date().getTime() - time.start;

        if (time.elapsed <= allowedTime) {
            const direction = _getDirection();
            if (direction) {
                preventIfCancelable(event);
                callback(direction);
            }
        }
    };

    const onTouchStart = (event) => {
        handleTouchStart(event.changedTouches[0].pageX, event.changedTouches[0].pageY);
    };

    const onTouchMove = (event) => {
        handleTouchMove(event, event.changedTouches[0].pageX, event.changedTouches[0].pageY);
    };

    const onTouchEnd = (event) => {
        handleTouchEnd(event, event.changedTouches[0].pageX, event.changedTouches[0].pageY);
    };

    const onMouseDown = (event) => {
        handleTouchStart(event.clientX, event.clientY);
    };

    const onMouseMove = (event) => {
        handleTouchMove(event, event.clientX, event.clientY);
    };

    const onMouseUp = (event) => {
        handleTouchEnd(event, event.clientX, event.clientY);
    };

    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);


    // include mouse events
    if (!isTouchDevice) {
        element.addEventListener('mousedown', onMouseDown);
        element.addEventListener('mousemove', onMouseMove);
        element.addEventListener('mouseup', onMouseUp);
    }


    return () => {
        element.removeEventListener('touchstart', onTouchStart);
        element.removeEventListener('touchmove', onTouchMove);
        element.removeEventListener('touchend', onTouchEnd);
        if (!isTouchDevice) {
            element.removeEventListener('mousedown', onMouseDown);
            element.removeEventListener('mousemove', onMouseMove);
            element.removeEventListener('mouseup', onMouseUp);
        }
    };
};


// Method 1
